// Auto-capture telemetry for user-visible error notifications.
//
// Every catch block that calls `notificationBus.error(...)` or
// `notificationBus.toast("error", ...)` previously left no record in
// Kibana — the api.ts fallback captured the underlying network/5xx event
// but engineers had no way to find the *user-visible* toast that surfaced
// from a JSON parse, an unexpected runtime exception, or any other
// site-specific failure. ~80 such catches exist across admin/superadmin/
// trader pages today.
//
// Rather than instrument 80 callsites individually, capture once at the
// boundary: every error notification ships as a captureMessage to Kibana
// with the toast title + message and a synthesized stack pointing at the
// caller. Level "warn" so the channel doesn't fire Telegram (Rule 11
// filters `level: "error"`) — engineers see the data in Kibana, the
// pager stays quiet for routine UI failures.
//
// Skipped titles are expected UX flows that aren't engineering signals:
//   - Session Expired, Account Blocked, Settings Updated — auth lifecycle
//   - Validation Error / *validation* — caller input mistakes, not bugs
import { captureMessage } from "./error-reporter"

type NotificationType = "success" | "error" | "info"

interface NotificationEvent {
  type: NotificationType
  title: string
  message: string
  nextPath?: string
  displayMode?: "modal" | "toast"
}

type Listener = (event: NotificationEvent) => void

const SKIP_CAPTURE_TITLES: ReadonlySet<string> = new Set([
  "session expired",
  "account blocked",
  "settings updated",
  "validation error",
])

function shouldCaptureNotification(type: NotificationType, title: string): boolean {
  if (type !== "error") return false
  const t = (title || "").trim().toLowerCase()
  if (SKIP_CAPTURE_TITLES.has(t)) return false
  if (t.includes("validation")) return false
  return true
}

class NotificationBus {
  private listeners: Set<Listener> = new Set()

  subscribe(listener: Listener) {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  notify(
    type: NotificationType,
    title: string,
    message: string,
    nextPath?: string,
    displayMode: "modal" | "toast" = "modal"
  ) {
    this.listeners.forEach((listener) => listener({ type, title, message, nextPath, displayMode }))

    if (shouldCaptureNotification(type, title)) {
      try {
        captureMessage(
          `UI ${displayMode}: ${title || "(no title)"} — ${message}`,
          "warn",
          { routeTag: "ui_notification", component: "ui_notification" }
        )
      } catch {
        /* never let telemetry break notification delivery */
      }
    }
  }

  success(title: string, message: string, nextPath?: string) {
    this.notify("success", title, message, nextPath, "modal")
  }

  error(title: string, message: string, nextPath?: string) {
    this.notify("error", title, message, nextPath, "modal")
  }

  info(title: string, message: string, nextPath?: string) {
    this.notify("info", title, message, nextPath, "modal")
  }

  toast(type: NotificationType, message: string, title?: string, nextPath?: string) {
    this.notify(type, title || "", message, nextPath, "toast")
  }
}

export const notificationBus = new NotificationBus()
